import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Leaf, RotateCcw, Stethoscope } from 'lucide-react';
import { useForm } from '../context/FormContext';
import { useAppConfig } from '../context/AppConfigContext';
import { useChatWidgetOffset } from '../hooks/useChatWidgetOffset';
import { buildWhatsAppHref } from '../components/funnel/whatsappMessage';
import {
  calculateNutrition,
  createCheckout,
  prepareCheckout,
  checkoutCallback,
  isOutOfStockError,
  type CheckoutRequest,
} from '../services/api';
import {
  createCart,
  fetchSellingPlanAllocations,
  resolveSellingPlan,
  encodeMonthlySellingPlans,
  StorefrontError,
  type CartLineInput,
  type CartAttributeInput,
  type CreatedCart,
} from '../api/storefront';
import { useAddonMode } from '../hooks/useAddonMode';
import { useUpdateMode } from '../hooks/useUpdateMode';
import { useMountedRef } from '../hooks/useMountedRef';
import { useSubmitGuard } from '../hooks/useSubmitGuard';
import { validateEmail } from '../utils/validation';
import { getSessionItem } from '../utils/safeStorage';
import { deriveDigestiveModifiers, getTransitionSchedule, pickCurrentFoodType } from '../utils/transitionSchedule';
import { AgeCategory } from '../types';
import { funnelOffersSupplements, getSelectedSupplementIds } from '../utils/supplements';
import { runAddonSubmit } from './checkout/useAddonSubmit';
import { runUpdateSubmit } from './checkout/useUpdateSubmit';
import { normalizeSelectedRecipes } from '../utils/variants';
import { calculateCheckoutPricing, clampDiscount } from '../utils/pricing';
import { formatPrice } from '../utils/formatters';
import { useCheckoutPricing } from '../checkout/useCheckoutPricing';
import { useCheckoutCalculation } from '../checkout/useCheckoutCalculation';
import { StockAdjustment, StockAdjustmentsNotice } from '../components/funnel/StockAdjustmentsNotice';
import { buildStockLimits, monthlyReserveBySize } from '../checkout/stockLimits';
import {
  droppedRecipeNames,
  monthlyDistributionFromPlan,
  normalizeDistributionSignature,
  recipeNameForProductId,
  selectedRecipesAfterPackEdit,
  selectedRecipesWithoutDropped,
} from '../utils/recipeDistribution';
import { reserveIncludesMonth, stockReserveScope } from '../utils/stockReserve';
import {
  buildExtraLineItems,
  buildShippingPayload,
  getDonationCheckoutAmount,
  validateCheckoutUrl,
} from '../checkout/checkoutDomain';
import { DEFAULT_APP_CONFIG } from '../config/defaultAppConfig';
import { calculateShippingPrice, ShippingResult } from '../utils/shipping';
import { ShippingDetectionSource } from '../types/config';
import { DonationAmount, useDonationConfig } from '../hooks/useDonationConfig';
import { buildApiPayload } from '../utils/formPayload';
import { clearDraft } from '../utils/localStorageDraft';
import StepNav from '../components/funnel/StepNav';
import { Phase2Header } from '../components/funnel/Phase2Header';
import { buildMultiPetCartPayload, snapshotsToPets, sumMultiPetPricing } from '../utils/multiPetCart';
import { buildPetSnapshot } from '../utils/petSnapshot';
import DonationSummary from './checkout/DonationSummary';
import DonationModal from './checkout/DonationModal';
import CartSummary from './checkout/CartSummary';
import ShippingSelector from './checkout/ShippingSelector';
import { UpdateSuccessScreen, UpdateDegradedScreen } from './checkout/CheckoutStatusScreens';

/**
 * Copo de nieve tachado para el badge "Sin frío" (lucide no trae SnowflakeOff).
 * Reproduce la geometría del Snowflake de lucide y añade una diagonal, respetando
 * su API (size / strokeWidth / currentColor) para encajar en el render de badges.
 */
function SnowflakeOff({ size = 24, ...props }: { size?: number } & React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <line x1="2" y1="12" x2="22" y2="12" />
      <line x1="12" y1="2" x2="12" y2="22" />
      <path d="m20 16-4-4 4-4" />
      <path d="m4 8 4 4-4 4" />
      <path d="m16 4-4 4-4-4" />
      <path d="m8 20 4-4 4 4" />
      <line x1="3" y1="3" x2="21" y2="21" />
    </svg>
  );
}

/**
 * UX timing: cuántos ms esperamos en checkout sin email antes de
 * abrir proactivamente el widget de LiveChat. Constante de UI pura
 * (no de negocio) — vive aquí en lugar de en system_configs.
 */
const LIVECHAT_GREETING_DELAY_MS = 60_000;
const EMAIL_SHAKE_INTERVAL_MS = 12_000;
const EMAIL_SHAKE_DURATION_MS = 800;
const IDEMPOTENCY_STORAGE_KEY = 'myl_idempotency_key';
const ANALYTICS_SESSION_STORAGE_KEY = 'myl_analytics_session';

// Selector de recetas = slot 10 (Step10RecetasV2). Antes estaba en 11 (Step11TuPlan),
// así que "editar recetas" y el bounce por stock llevaban a "Tu Plan", no a Recetas.
const RECIPE_PICKER_STEP = 10;

// Tope UX de mascotas por pedido (el backend admite hasta 20 vía num_mascotas;
// mascotas_json tiene un límite de 4000 chars que ~13 mascotas agotarían).
const MAX_PETS_PER_ORDER = 4;

// StepNav labels: RECETAS(0)→10, TU PLAN(1)→11, EXTRAS(2)→13 [Step12Extras].
const STEP_NAV_MAP: Record<number, number> = { 0: 10, 1: 11, 2: 13 };

function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * The user's selected recipes can't produce a valid plan. Bounces back to
 * step 10 (recipe picker) so the user can re-select. Covers:
 *   - 409 OUT_OF_STOCK (some selected products ran out of stock)
 *   - 400 "no están disponibles" (selected variant IDs don't exist in DB)
 *   - 404 "No se encontraron recetas válidas con datos nutricionales completos"
 *     (selected variants exist but lack nutritional data — e.g. supplements
 *     leaked into the recipes list)
 */
function isRecipeCatalogError(err: unknown): boolean {
  if (isOutOfStockError(err)) return true;
  if (!(err instanceof Error)) return false;
  return err.message.includes('no están disponibles') || err.message.includes('No se encontraron recetas válidas');
}

export default function Step13Checkout() {
  const {
    formData,
    updateFormData,
    setCurrentStep,
    petsCompleted,
    addPetSnapshot,
    removePetSnapshot,
    replaceCurrentWithPetSnapshot,
    resetForNewPet,
    multiPetIntent,
    setMultiPetIntent,
    restorePetForEditing,
  } = useForm();
  const { t, config, recipes, refreshConfig, platform } = useAppConfig();
  // El gate de App.tsx garantiza config 'ready' al llegar aquí; un refresh
  // fallido a mitad de sesión conserva la última config buena, así que el
  // único bloqueo legítimo es que el catálogo esté realmente vacío — NO
  // lastError (un blip del poll de 60s no debe deshabilitar el checkout).
  const catalogUnavailable = recipes.length === 0;
  const addon = useAddonMode();
  const updateCtx = useUpdateMode();
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateDegradedMode, setUpdateDegradedMode] = useState(false);
  const [morePetsChoice, setMorePetsChoice] = useState<'yes' | 'no' | null>(null);
  const [showMultipetNoteDetail, setShowMultipetNoteDetail] = useState(false);

  const isMultiPet = petsCompleted.length > 0;
  // Kill switch admin (multipet_rules.enabled): oculta los bloques de "añadir
  // otra mascota" y muestra el aviso de repetir el test. El backend además
  // rechaza checkouts con num_mascotas > 1 cuando está desactivado.
  const multipetEnabled = config.multipet_rules?.enabled !== false;

  const subscriptionStart: 'transition' | 'full' = formData.subscriptionStart || 'transition';
  const isTransition = subscriptionStart === 'transition';

  const transitionScheduleDays = useMemo(() => {
    if (!isTransition) return 0;
    const baseFood = pickCurrentFoodType(formData.currentFood);
    const digestive = deriveDigestiveModifiers(formData.pathologies, formData.subPathologies);
    return getTransitionSchedule(baseFood, {
      isCachorro: formData.ageCategory === AgeCategory.CACHORRO,
      isSenior: formData.ageCategory === AgeCategory.SENIOR,
      ...digestive,
    }).length;
  }, [isTransition, formData.currentFood, formData.ageCategory, formData.pathologies, formData.subPathologies]);

  const INITIATION_PACK_DISCOUNT = clampDiscount(config.pricing_rules.initiation_discount);
  const MONTHLY_SUBSCRIPTION_DISCOUNT = clampDiscount(config.pricing_rules.monthly_discount);
  const INITIATION_DAYS = config.pricing_rules.initiation_days;
  const SUBSCRIPTION_DAYS = config.pricing_rules.subscription_days;

  const checkoutPaused = Boolean(config.runtime_controls?.checkout_paused);
  const checkoutPausedMessage =
    config.runtime_controls?.checkout_paused_message || DEFAULT_APP_CONFIG.runtime_controls.checkout_paused_message;
  // Único origen del teléfono: contact_rules.whatsapp_phone. Vacío = el aviso
  // de pausa se muestra sin botón de WhatsApp, en vez de con un enlace roto.
  const whatsappUrl = buildWhatsAppHref(config.contact_rules?.whatsapp_phone || '', '');

  const donation = useDonationConfig(config.donation_rules, { disabled: platform === 'prestashop' });
  const donationRecurringVariantId = donation.recurringVariantId;
  const donationOneTimeVariantIds = donation.oneTimeVariantIds;
  const hasRecurringDonation = donation.hasRecurring;
  const hasAnyOneTimeDonation = donation.hasAnyOneTime;
  const showDonationToggle = donation.showToggle;
  const showDonationWidget = donation.hasAny;

  const [email, setEmail] = useState(formData.email || '');
  // El setTimeout interno se guarda y se limpia en cleanup para evitar un state update tras unmount.
  const [shouldShakeEmail, setShouldShakeEmail] = useState(false);
  useEffect(() => {
    if (email) return;
    let resetTimer: number | undefined;
    const interval = window.setInterval(() => {
      setShouldShakeEmail(true);
      resetTimer = window.setTimeout(() => setShouldShakeEmail(false), EMAIL_SHAKE_DURATION_MS);
    }, EMAIL_SHAKE_INTERVAL_MS);
    return () => {
      window.clearInterval(interval);
      if (resetTimer !== undefined) window.clearTimeout(resetTimer);
    };
  }, [email]);
  const acceptsMarketing = true;
  const [donationMode, setDonationMode] = useState<'recurring' | 'one-time'>(
    hasRecurringDonation ? 'recurring' : 'one-time',
  );
  const [donateRecurringAmount, setDonateRecurringAmount] = useState<DonationAmount | null>(null);
  const [donateOneTimeAmount, setDonateOneTimeAmount] = useState<DonationAmount | null>(null);
  const [donateCustomAmount, setDonateCustomAmount] = useState('');
  const [donationModalOpen, setDonationModalOpen] = useState(false);
  const [donationInfoOpen, setDonationInfoOpen] = useState(false);
  const { isSubmitting, guard } = useSubmitGuard();
  const mountedRef = useMountedRef();

  useEffect(() => {
    if (!donationModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDonationModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [donationModalOpen]);
  const [error, setError] = useState('');
  // Recipes the backend adapted for stock at submit time (size change or drop); shown next
  // to the summary so the tutor reviews the refreshed pack before confirming again.
  const [submitAdjustments, setSubmitAdjustments] = useState<StockAdjustment[]>([]);

  // The mobile checkout bar is taller than the shared funnel CTA (it shows the
  // total plus validation hints): publish its height so LiveChat sits above it.
  const mobileCtaRef = useRef<HTMLDivElement>(null);
  useChatWidgetOffset(mobileCtaRef);

  // Greeting proactivo de LiveChat: si el cliente lleva 60s en checkout sin
  // rellenar email, maximiza el widget para invitar a preguntar. Solo aplica
  // con LiveChat activo; con el botón de WhatsApp no hay nada que maximizar y
  // el temporizador correría para nada.
  const liveChatIsActive = config.chat_widget_rules?.active_widget === 'livechat';
  useEffect(() => {
    if (!liveChatIsActive) return;
    const timer = window.setTimeout(() => {
      if (email) return;
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const lc = (window as any).LiveChatWidget;
        if (lc && typeof lc.call === 'function') {
          lc.call('maximize');
        }
      } catch {
        // silencioso: si el widget no esta cargado, no pasa nada
      }
    }, LIVECHAT_GREETING_DELAY_MS);
    return () => window.clearTimeout(timer);
    // Solo depende del email inicial: si el usuario lo rellena despues, el
    // componente sigue montado pero ya no queremos molestarlo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveChatIsActive]);

  const [idempotencyKey, setIdempotencyKey] = useState(() => {
    try {
      const stored = sessionStorage.getItem(IDEMPOTENCY_STORAGE_KEY);
      if (stored) return stored;
      const key = generateIdempotencyKey();
      sessionStorage.setItem(IDEMPOTENCY_STORAGE_KEY, key);
      return key;
    } catch {
      return generateIdempotencyKey();
    }
  });

  const normalizedSelectedRecipeIds = useMemo(
    () => normalizeSelectedRecipes(formData.selectedRecipes),
    [formData.selectedRecipes],
  );

  const { isCalculating, pricingError, droppedRecipes, sizeChanges } = useCheckoutCalculation({
    formData,
    recipes,
    config,
    isTransition,
    normalizedSelectedRecipeIds,
    updateFormData,
    setCurrentStep,
    refreshConfig,
  });

  const droppedNames = useMemo(() => droppedRecipeNames(droppedRecipes, recipes), [droppedRecipes, recipes]);
  // Entry notice: recipes the re-plan moved to their other size for stock, then the dropped ones.
  const entryAdjustments = useMemo<StockAdjustment[]>(
    () => [
      ...sizeChanges.map(
        (item): StockAdjustment => ({
          id: item.recipe_id,
          name: recipeNameForProductId(item.recipe_id, recipes) ?? item.recipe_name,
          kind: 'size',
          sizeG: item.resolved_size_g as number,
          otherSizeG: item.resolved_size_g === 800 ? 400 : 800,
        }),
      ),
      ...droppedRecipes.map(
        (item, index): StockAdjustment => ({ id: item.recipe_id, name: droppedNames[index], kind: 'dropped' }),
      ),
    ],
    [sizeChanges, droppedRecipes, droppedNames, recipes],
  );
  // One notice for the checkout: what the submit adapted first, then what the re-plan on
  // entry adapted (a prune at submit re-plans, which may move another recipe's size).
  const checkoutAdjustments = useMemo<StockAdjustment[]>(
    () => [
      ...submitAdjustments,
      ...entryAdjustments.filter((item) => !submitAdjustments.some((adjusted) => adjusted.id === item.id)),
    ],
    [submitAdjustments, entryAdjustments],
  );

  const pricing = useCheckoutPricing({
    formData,
    recipes,
    config,
    isTransition,
    normalizedSelectedRecipeIds,
  });

  const {
    initiationPrice,
    initiationOriginalPrice,
    monthlyDiscountedPrice,
    supplementsOneOffTotal,
    todayTotal,
    discountAmount,
    effectiveRecipeLines,
    hasSupplements,
  } = pricing;

  const multiPetTotals = useMemo(
    () => sumMultiPetPricing(petsCompleted, initiationPrice, monthlyDiscountedPrice),
    [petsCompleted, initiationPrice, monthlyDiscountedPrice],
  );
  const orderFoodAndSupplementsTotal = isMultiPet
    ? multiPetTotals.totalInitiation + supplementsOneOffTotal
    : todayTotal;

  const shippingRules = config.shipping_rules;
  const isShippingActive = Boolean(shippingRules?.enabled);
  const enabledCountries = useMemo(() => (shippingRules?.countries || []).filter((c) => c.enabled), [shippingRules]);
  const [shippingCountryUI, setShippingCountryUI] = useState<string>(() => {
    if (formData.shippingCountry) return formData.shippingCountry;
    const cached = getSessionItem<{ country?: string } | null>('myl_geo', null);
    const detected = String(cached?.country || '').toUpperCase();
    if (detected && enabledCountries.some((c) => c.code.toUpperCase() === detected)) {
      return detected;
    }
    return shippingRules?.default_country || 'ES';
  });
  const [shippingPostalCodeUI, setShippingPostalCodeUI] = useState<string>(formData.shippingPostalCode || '');
  const [shippingDetectionSource, setShippingDetectionSource] = useState<ShippingDetectionSource>(
    formData.shippingDetectionSource || (formData.shippingCountry ? 'manual' : 'geoip'),
  );

  const shippingResult: ShippingResult | null = useMemo(() => {
    if (!isShippingActive || !shippingRules) return null;
    return calculateShippingPrice(shippingCountryUI, shippingPostalCodeUI, orderFoodAndSupplementsTotal, shippingRules);
  }, [isShippingActive, shippingRules, shippingCountryUI, shippingPostalCodeUI, orderFoodAndSupplementsTotal]);

  const selectedCountryCfg = useMemo(
    () => enabledCountries.find((c) => c.code.toUpperCase() === shippingCountryUI.toUpperCase()) || null,
    [enabledCountries, shippingCountryUI],
  );
  const countryHasZones = Boolean(selectedCountryCfg && selectedCountryCfg.zones.length > 0);
  // The address is optional and never blocks the order. Until a postal code is typed for a
  // country with zones nothing is priced: an empty code would fall through to the country
  // base price and skip disabled zones, so the summary asks for the address instead.
  const shippingAddressPending = isShippingActive && countryHasZones && shippingPostalCodeUI.trim().length === 0;
  const shippingPrice =
    isShippingActive && !shippingAddressPending && shippingResult?.supported
      ? Number((shippingResult.shipping_price || 0).toFixed(2))
      : 0;
  const shippingUnavailable =
    isShippingActive && !shippingAddressPending && shippingResult !== null && !shippingResult.supported;
  // Monthly recurring plan shipping (monthly total, not the first order): decides
  // whether the "free shipping" claim in the monthly block is true for the zone.
  const monthlyShippingResult = useMemo(() => {
    if (!isShippingActive || !shippingRules) return null;
    const monthlyTotal = isMultiPet && multiPetTotals ? multiPetTotals.totalMonthly : monthlyDiscountedPrice;
    return calculateShippingPrice(shippingCountryUI, shippingPostalCodeUI, monthlyTotal, shippingRules);
  }, [
    isShippingActive,
    shippingRules,
    shippingCountryUI,
    shippingPostalCodeUI,
    isMultiPet,
    multiPetTotals,
    monthlyDiscountedPrice,
  ]);
  const monthlyShippingFree =
    !shippingAddressPending && monthlyShippingResult?.supported === true && monthlyShippingResult.is_free === true;
  const monthlyShippingPrice =
    !shippingAddressPending && monthlyShippingResult?.supported === true && !monthlyShippingResult.is_free
      ? Number((monthlyShippingResult.shipping_price || 0).toFixed(2))
      : 0;
  const donationCheckoutAmount = getDonationCheckoutAmount(
    {
      donationMode,
      donateRecurringAmount,
      donateOneTimeAmount,
      donateCustomAmount,
    },
    {
      minEur: config.donation_rules?.min_eur,
      maxEur: config.donation_rules?.max_eur,
    },
  );
  const displayTodayTotal = Number((orderFoodAndSupplementsTotal + shippingPrice + donationCheckoutAmount).toFixed(2));

  const dailyCost = isTransition ? initiationPrice / INITIATION_DAYS : monthlyDiscountedPrice / SUBSCRIPTION_DAYS;
  const savings = Math.max(0, discountAmount);

  const isEmailValid = validateEmail(email);
  const errorIsEmail = error.toLowerCase().includes('email');
  const isAddonOrUpdate = addon.isAddon || updateCtx.isUpdateMode;
  const ctaDisabled = isAddonOrUpdate
    ? isSubmitting || isCalculating
    : !isEmailValid || isSubmitting || isCalculating || checkoutPaused || catalogUnavailable;
  const checkoutNeedsEmail = !isAddonOrUpdate && !isEmailValid;

  let submitLabel: string;
  if (updateCtx.isUpdateMode) {
    submitLabel = t('step13c.submit_update', 'ACTUALIZAR PLAN');
  } else if (addon.isAddon) {
    submitLabel = t('step13c.submit_addon', 'AÑADIR A MI PLAN');
  } else {
    submitLabel = t('step13c.submit', 'CONFIRMAR PEDIDO');
  }

  let heroTitle: string;
  if (addon.isAddon) {
    heroTitle = t('step13c.title_addon', '¿Todo correcto para {nombre}? 🐾', {
      nombre: formData.nombre || 'tu peludo',
    });
  } else if (isMultiPet) {
    heroTitle = t('step13c.title_multipet', '¡Todo listo para tu familia! 🐾');
  } else {
    heroTitle = t('step13c.title_compact', '¡Todo listo para {nombre}! 🐾', {
      nombre: formData.nombre || 'tu perro',
    });
  }

  // --- Submit handler ---
  const handleSubmit = async () => {
    if (ctaDisabled) return;
    // ── Addon flow ─────────────────────────────────────────────────────
    if (addon.isAddon && addon.subscriptionId) {
      if (normalizedSelectedRecipeIds.length === 0) {
        setError(t('step13c.error_recipes', 'Selecciona al menos una receta antes de continuar'));
        return;
      }
      setError('');
      const addonSubscriptionId = addon.subscriptionId;
      await guard(() =>
        runAddonSubmit({
          subscriptionId: addonSubscriptionId,
          formData,
          normalizedSelectedRecipeIds,
          config,
          mountedRef,
          onError: setError,
        }),
      );
      return;
    }

    // ── Update flow ────────────────────────────────────────────────────
    if (updateCtx.isUpdateMode && updateCtx.petId && updateCtx.subscriptionId) {
      setError('');
      const updatePetId = updateCtx.petId;
      const updateSubscriptionId = updateCtx.subscriptionId;
      await guard(async () => {
        const result = await runUpdateSubmit({
          subscriptionId: updateSubscriptionId,
          petId: updatePetId,
          formData,
          normalizedSelectedRecipeIds,
          config,
          mountedRef,
          onError: setError,
        });
        if (result === null) return;
        if (result.degraded) {
          setUpdateDegradedMode(true);
        } else {
          setUpdateSuccess(true);
        }
      });
      return;
    }

    if (checkoutPaused) {
      setError(
        t(
          'step13c.error_paused',
          'Checkout pausado por mantenimiento. Inténtalo en unos minutos o contáctanos por WhatsApp.',
        ),
      );
      return;
    }
    setError('');
    setSubmitAdjustments([]);
    const configSupplements = config.supplement_rules?.supplements;
    // Checkout identifies supplements by their canonical product.id (UUID). The
    // backend resolves the per-channel product from the channel bindings, so we
    // no longer send Shopify variant ids for supplements.
    const selectedSupplementIds = getSelectedSupplementIds(formData.selectedSupplements || [], configSupplements);

    if (!email || !isEmailValid) {
      setError(t('step13c.error_email', 'Por favor, introduce un email valido'));
      return;
    }

    if (normalizedSelectedRecipeIds.length === 0) {
      setError(t('step13c.error_recipes', 'Selecciona al menos una receta antes de continuar'));
      return;
    }

    let analyticsSessionId: string | null = null;
    try {
      analyticsSessionId = sessionStorage.getItem(ANALYTICS_SESSION_STORAGE_KEY);
    } catch {
      analyticsSessionId = null;
    }

    await guard(async () => {
      try {
        updateFormData({
          email,
          acceptsMarketing,
          ...(isShippingActive
            ? {
                shippingCountry: shippingCountryUI,
                shippingPostalCode: shippingPostalCodeUI,
                shippingZoneId: shippingResult?.zone_id ?? null,
                shippingPrice: shippingResult?.shipping_price,
                shippingDetectionSource,
              }
            : {}),
        });

        let calculationId = formData.calculationId;
        let authoritativeDistribution = formData.initiationPackDistribution;
        if (!calculationId) {
          const payload = buildApiPayload(formData, {
            selectedRecipes: normalizedSelectedRecipeIds,
            email,
            acceptsMarketing,
            platform: config.effective_platform,
          });
          const calculation = await calculateNutrition(payload);
          calculationId = calculation.calculation_id;
          const droppedAtSubmit = calculation.dropped_recipes ?? [];
          if (droppedAtSubmit.length > 0) {
            const remaining = selectedRecipesWithoutDropped(normalizedSelectedRecipeIds, droppedAtSubmit, recipes);
            if (remaining.length > 0 && remaining.length < normalizedSelectedRecipeIds.length) {
              // The persisted plan no longer has these recipes: prune the selection and let
              // the tutor confirm the recalculated pack before paying.
              updateFormData({
                selectedRecipes: remaining,
                calculationId: undefined,
                packDistributionEdited: false,
              });
              const droppedNamesAtSubmit = droppedRecipeNames(droppedAtSubmit, recipes);
              setSubmitAdjustments(
                droppedAtSubmit.map((item, index) => ({
                  id: item.recipe_id,
                  name: droppedNamesAtSubmit[index],
                  kind: 'dropped',
                })),
              );
              return;
            }
            // An unmappable drop (the pruned selection is empty or unchanged) cannot be
            // resolved locally: bounce to the picker so it re-syncs with the catalog.
            updateFormData({ selectedRecipes: [], calculationId: undefined, recipeStockBounced: true });
            try {
              await refreshConfig({ silent: true });
            } catch {
              /* non-fatal */
            }
            if (!mountedRef.current) return;
            setCurrentStep(RECIPE_PICKER_STEP);
            return;
          }
          const recalculatedMonthlyPrice = Number(calculation.monthly_price.toFixed(2));
          if (recalculatedMonthlyPrice === 0 || !calculation.recipes || calculation.recipes.length === 0) {
            updateFormData({
              selectedRecipes: [],
              calculationId: undefined,
              recipeStockBounced: true,
            });
            setCurrentStep(RECIPE_PICKER_STEP);
            return;
          }
          const backendDistribution = Object.fromEntries(
            calculation.recipes
              .map((recipe) => [recipe.recipe_id, isTransition ? recipe.initiation_cans : recipe.monthly_cans] as const)
              .filter(([, quantity]) => quantity > 0),
          );
          // A recipe the backend just moved to its other size for stock: the summary the
          // tutor read still shows the old size, so refresh it and ask for a second confirm.
          // Only the pack on screen counts as known: the recommended one may already carry
          // the other size from an earlier submit while the tutor put the old size back.
          const shownDistribution = formData.initiationPackDistribution ?? {};
          const knownProductIds = new Set(
            Object.keys(
              Object.keys(shownDistribution).length > 0
                ? shownDistribution
                : (formData.recommendedInitiationPackDistribution ?? {}),
            ),
          );
          const sizeChangedAtSubmit = calculation.recipes.filter(
            (recipe) =>
              recipe.size_reason === 'stock_fallback' &&
              recipe.resolved_size_g &&
              !knownProductIds.has(recipe.recipe_id),
          );
          // Keep the user's edited pack when they changed quantities; otherwise
          // adopt the backend's recommended cans. Checkout accepts the custom
          // distribution via allow_custom_initiation_distribution. An edited pack
          // keyed by a size that just ran out is replaced by the recommended one.
          authoritativeDistribution =
            formData.packDistributionEdited && formData.initiationPackDistribution && sizeChangedAtSubmit.length === 0
              ? formData.initiationPackDistribution
              : backendDistribution;
          const recalculated = calculateCheckoutPricing({
            initiationPackDistribution: authoritativeDistribution,
            initiationPackPrice: isTransition ? calculation.initiation_pack_price : calculation.monthly_price,
            monthlyPlanPrice: recalculatedMonthlyPrice,
            supplementsOneOffTotal: formData.supplementsOneOffTotal,
            recipes,
            config,
            isInitiation: isTransition,
          });
          updateFormData({
            initiationPackPrice: recalculated.initiationPrice,
            monthlyPlanPrice: recalculatedMonthlyPrice,
            initiationPackDistribution: authoritativeDistribution,
            recommendedInitiationPackDistribution: backendDistribution,
            monthlyPlanDistribution: monthlyDistributionFromPlan(calculation.recipes),
            monthlyPackSummary: calculation.monthly_pack ?? undefined,
            initiationPackSummary: calculation.initiation_pack ?? undefined,
            packConservation: calculation.pack_conservation ?? undefined,
            ...(sizeChangedAtSubmit.length > 0 ? { packDistributionEdited: false } : {}),
          });
          if (sizeChangedAtSubmit.length > 0) {
            setSubmitAdjustments(
              sizeChangedAtSubmit.map((recipe) => ({
                id: recipe.recipe_id,
                name: recipeNameForProductId(recipe.recipe_id, recipes) ?? recipe.recipe_name,
                kind: 'size',
                sizeG: recipe.resolved_size_g as number,
                otherSizeG: recipe.resolved_size_g === 800 ? 400 : 800,
              })),
            );
            return;
          }
        }

        let checkout;
        let multiPetPayload = null;
        // Checkout client-side (flag admin): el navegador crea el carrito Shopify.
        // SOLO aplica a Shopify (Storefront API) — en PrestaShop se usa siempre el
        // checkout server-side de dual-checkout (createCheckout).
        const clientSideEnabled =
          platform === 'shopify' && Boolean(config.runtime_controls?.checkout_client_side_enabled);
        // Dominios de checkout que el servidor autoriza (solo en client-side); se
        // pasan a la validación común de la URL para no rechazar dominios legítimos.
        let serverAllowedDomains: string[] | undefined;
        if (isMultiPet) {
          try {
            multiPetPayload = buildMultiPetCartPayload(snapshotsToPets(petsCompleted, formData), recipes);
          } catch (err) {
            console.error('[Step13] buildMultiPetCartPayload failed:', err);
            throw new Error(
              t(
                'step13c.multipet_payload_error',
                'No hemos podido preparar el pedido con todas las mascotas. Revisa los planes o vuelve a intentarlo.',
              ),
            );
          }
        }

        // Distribution keyed by canonical product.id (UUID): for multipet it is
        // the aggregated per-productId lines; single-pet it is formData's own
        // productId-keyed distribution.
        const mergedDistribution: Record<string, number> | undefined = multiPetPayload
          ? Object.fromEntries(multiPetPayload.lines.map((l) => [l.productId, l.quantity]))
          : authoritativeDistribution;

        // ONE generic checkout call for every platform. The backend resolves the
        // effective platform (respecting force modes), maps the canonical
        // selection to that store's ids via the channel bindings, and routes the
        // checkout — the frontend only forwards the detected origin `platform`.
        const extraLineItems = buildExtraLineItems(
          {
            donationMode,
            donateRecurringAmount,
            donateOneTimeAmount,
            donateCustomAmount,
            donationRecurringVariantId,
            donationOneTimeVariantIds,
          },
          {
            minEur: config.donation_rules?.min_eur,
            maxEur: config.donation_rules?.max_eur,
          },
        );

        const shippingPayload = buildShippingPayload({
          isShippingActive,
          shippingResult,
          shippingCountryUI,
          shippingPostalCodeUI,
          shippingDetectionSource,
        });

        // Body común a createCheckout (legacy) y prepareCheckout (client-side).
        const body: CheckoutRequest = {
          calculation_id: calculationId,
          email,
          dog_name: formData.nombre || 'Perro',
          accepts_marketing: acceptsMarketing,
          is_initiation_pack: isTransition,
          initiation_pack_distribution: mergedDistribution,
          selected_supplement_ids: selectedSupplementIds,
          platform,
          analytics_session_id: analyticsSessionId,
          ...(extraLineItems.length > 0 ? { extra_line_items: extraLineItems } : {}),
          ...shippingPayload,
          ...(multiPetPayload
            ? {
                num_mascotas: Number(multiPetPayload.attributes.num_mascotas),
                mascotas_json: multiPetPayload.attributes.mascotas_json,
              }
            : {}),
        };

        if (clientSideEnabled) {
          // El navegador crea el carrito Shopify desde la IP del comprador.
          const spec = await prepareCheckout(body, idempotencyKey);
          const leadId = spec.lead_id;
          serverAllowedDomains = spec.allowed_checkout_domains;

          // Toda la secuencia navegador→Shopify (allocations + cartCreate) va en
          // UN solo try/catch que reporta CUALQUIER StorefrontError al servidor.
          // La query de allocations es la PRIMERA llamada y la más propensa a un
          // 430/throttle; el reporte del checkout-callback es lo que recupera la
          // observabilidad —throttle/430 del navegador— que motivó todo este flujo.
          let cart: CreatedCart;
          try {
            const lines: CartLineInput[] = [];
            const monthlyPlans: Record<string, string> = {};

            for (const rl of spec.recipe_lines) {
              // Una sola query de allocations por receta (reutilizada para el plan
              // de hoy y el mensual).
              const allocations = await fetchSellingPlanAllocations(
                spec.storefront.graphql_url,
                spec.storefront.public_access_token,
                rl.variant_gid,
              );

              const sellingPlanId = resolveSellingPlan(allocations, {
                targetDiscount: rl.target_discount,
                basePrice: rl.base_price,
                onMismatch: () =>
                  void checkoutCallback({
                    lead_id: leadId,
                    outcome: 'failure',
                    error_code: 'selling_plan_discount_mismatch',
                  }),
              });
              if (!sellingPlanId) {
                // Equivale al 400 de build_cart_lines cuando no hay plan resoluble.
                void checkoutCallback({ lead_id: leadId, outcome: 'failure', error_code: 'plan_not_found' });
                throw new Error(t('step13c.error_plan', 'No se pudo activar la suscripción. Inténtalo de nuevo.'));
              }

              lines.push({
                merchandiseId: rl.variant_gid,
                quantity: rl.quantity,
                sellingPlanId,
                attributes: rl.line_attributes,
              });

              if (rl.resolve_monthly_plan && rl.monthly_discount != null) {
                const monthlyPlanId = resolveSellingPlan(allocations, {
                  targetDiscount: rl.monthly_discount,
                  basePrice: rl.base_price,
                  onMismatch: () =>
                    void checkoutCallback({
                      lead_id: leadId,
                      outcome: 'failure',
                      error_code: 'selling_plan_discount_mismatch',
                    }),
                });
                if (monthlyPlanId) monthlyPlans[rl.variant_numeric] = monthlyPlanId;
              }
            }

            for (const ol of spec.oneoff_lines) {
              lines.push({
                merchandiseId: ol.variant_gid,
                quantity: ol.quantity,
                attributes: ol.line_attributes,
              });
            }

            const attributes: CartAttributeInput[] = spec.cart_attributes.map((a) => ({ key: a.key, value: a.value }));
            if (Object.keys(monthlyPlans).length > 0) {
              attributes.push({ key: 'monthly_selling_plans', value: encodeMonthlySellingPlans(monthlyPlans) });
            }

            cart = await createCart({
              graphqlUrl: spec.storefront.graphql_url,
              token: spec.storefront.public_access_token,
              buyerEmail: spec.buyer_email,
              lines,
              attributes,
            });
          } catch (shopErr) {
            // Reporta al servidor CUALQUIER fallo del Storefront desde el navegador
            // (allocations O cartCreate). Los errores de stock (OutOfStockError) NO
            // son StorefrontError → no se reportan aquí; el catch común los trata
            // como rebote a selección de recetas. plan_not_found ya se reportó antes
            // de su throw (Error plano, no StorefrontError → sin doble reporte).
            if (shopErr instanceof StorefrontError) {
              void checkoutCallback({
                lead_id: leadId,
                outcome: 'failure',
                error_code: shopErr.code,
                error_detail: shopErr.message,
              });
            }
            throw shopErr;
          }

          const cartHostname = validateCheckoutUrl(cart.checkoutUrl, spec.allowed_checkout_domains);
          if (!cartHostname) {
            void checkoutCallback({
              lead_id: leadId,
              outcome: 'failure',
              error_code: 'domain_mismatch',
              checkout_url: cart.checkoutUrl,
              cart_id: cart.id,
            });
            setError(t('step13c.error_domain', 'URL de checkout no valida.'));
            return;
          }

          void checkoutCallback({
            lead_id: leadId,
            outcome: 'success',
            checkout_url: cart.checkoutUrl,
            cart_id: cart.id,
          });

          checkout = { checkout_url: cart.checkoutUrl, lead_id: leadId, discount_code: '' };
        } else {
          checkout = await createCheckout(body, idempotencyKey);
        }

        const checkoutUrl = checkout.checkout_url;
        const checkoutHostname = validateCheckoutUrl(checkoutUrl, serverAllowedDomains);
        if (!checkoutHostname) {
          setError(t('step13c.error_domain', 'URL de checkout no valida.'));
          return;
        }

        const newKey = generateIdempotencyKey();
        try {
          sessionStorage.setItem(IDEMPOTENCY_STORAGE_KEY, newKey);
        } catch {}
        setIdempotencyKey(newKey);

        setMultiPetIntent(false);
        clearDraft();
        window.location.href = checkoutUrl;
      } catch (err: unknown) {
        console.error('Error creating checkout:', err);
        if (isRecipeCatalogError(err)) {
          if (!mountedRef.current) return;
          setError(
            t(
              'step13c.error_stock',
              'Por falta de stock no podemos servir las recetas que habías elegido. Te llevamos al selector para que elijas otras.',
            ),
          );
          updateFormData({ selectedRecipes: [], calculationId: undefined, recipeStockBounced: true });
          try {
            await refreshConfig({ silent: true });
          } catch {
            /* non-fatal */
          }
          if (!mountedRef.current) return;
          setCurrentStep(RECIPE_PICKER_STEP);
          return;
        }
        const axiosErr = err as { response?: { headers?: Record<string, string> } };
        const requestId = axiosErr?.response?.headers?.['x-request-id'];
        const refSuffix = requestId ? ` (ref: ${requestId.slice(0, 8)})` : '';
        const errorMsg = err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMsg + refSuffix);
      }
    });
  };

  const handleStepClick = (stepIndex: number) => {
    const target = STEP_NAV_MAP[stepIndex];
    if (target !== undefined) setCurrentStep(target);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setEmail(next);
    if (error && validateEmail(next)) setError('');
  };

  // "Atrás" goes to the previous funnel step: Extras (13) normally, or straight
  // to ComoEmpezar (12) when the effective channel offers no supplements (the
  // Extras step is skipped).
  const handleBack = () => setCurrentStep(funnelOffersSupplements(config.supplement_rules) ? 13 : 12);
  const handleEditRecipes = () => setCurrentStep(RECIPE_PICKER_STEP);
  const handleUpdateCurrentDistribution = (distribution: Record<string, number>) => {
    if (Object.keys(distribution).length === 0) return;
    // Mark the pack as user-edited so the submit keeps this distribution instead of
    // the backend's recommended cans. Pricing and the day/kcal bars recompute from
    // initiationPackDistribution via useCheckoutPricing. selectedRecipes only
    // changes when a recipe was removed: touching it re-runs the plan calculation,
    // which would wipe this edit.
    const nextSelectedRecipes = selectedRecipesAfterPackEdit({
      selectedRecipes: normalizedSelectedRecipeIds,
      previousDistribution: formData.initiationPackDistribution,
      nextDistribution: distribution,
      recipes,
    });
    // Applying the recommended pack itself (e.g. after "Restaurar original") is not an edit.
    const recommended = formData.recommendedInitiationPackDistribution;
    const isEdited =
      !recommended || normalizeDistributionSignature(recommended) !== normalizeDistributionSignature(distribution);
    updateFormData({
      initiationPackDistribution: distribution,
      ...(nextSelectedRecipes ? { selectedRecipes: nextSelectedRecipes } : {}),
      packDistributionEdited: isEdited,
    });
  };
  const handleOpenDonationModal = () => setDonationModalOpen(true);
  const handleCloseDonationModal = () => setDonationModalOpen(false);
  const handleOpenDonationInfo = () => setDonationInfoOpen(true);
  const handleCloseDonationInfo = () => setDonationInfoOpen(false);
  const handleToggleRecurringDonation = (checked: boolean) => setDonateRecurringAmount(checked ? 1 : null);

  // Hard cap for the edit-menus modal: the backend publishes the net quantity with
  // every stock rule already applied (same source as the checkout quantity gate).
  // `config.products` may be missing on a partial config (tests, older backend).
  const reserveScope = stockReserveScope(config.inventory_rules);
  const stockLimits = useMemo(
    () =>
      buildStockLimits({
        products: config.products ?? [],
        petsCompleted,
        reserve: reserveIncludesMonth(reserveScope, isTransition)
          ? monthlyReserveBySize(formData.monthlyPlanDistribution ?? {}, recipes)
          : {},
      }),
    [config.products, petsCompleted, reserveScope, isTransition, formData.monthlyPlanDistribution, recipes],
  );

  const handleAddPetFromCheckout = () => {
    setMorePetsChoice(null);
    const planData = {
      distribution: (formData.initiationPackDistribution || {}) as Record<string, number>,
      discountedInitiationPrice: initiationPrice,
      monthlyPrice: monthlyDiscountedPrice,
      sizeChoice: (formData.sizeChoice || {}) as Record<string, string>,
    };
    const snapshot = buildPetSnapshot(formData, planData, recipes);
    addPetSnapshot(snapshot);
    resetForNewPet();
    setCurrentStep(1);
  };

  const handleNoMorePets = () => {
    setMorePetsChoice('no');
    setMultiPetIntent(false);
  };

  const handleRemoveCurrentPetFromCheckout = () => {
    const fallbackPet = petsCompleted[petsCompleted.length - 1];
    if (fallbackPet) replaceCurrentWithPetSnapshot(fallbackPet.id, 13);
  };

  if (updateSuccess) {
    return <UpdateSuccessScreen nombre={formData.nombre || 'tu perro'} t={t} />;
  }

  if (updateDegradedMode) {
    return <UpdateDegradedScreen nombre={formData.nombre || 'tu perro'} t={t} />;
  }

  const supplementRules = config.supplement_rules?.supplements || [];

  let emailBorderClasses: string;
  if (errorIsEmail && !isEmailValid) {
    emailBorderClasses = 'border-red-500 focus:border-red-500 focus:ring-red-200';
  } else if (!email) {
    emailBorderClasses = 'border-amber-500 focus:border-amber-500 focus:ring-amber-200';
  } else {
    emailBorderClasses = 'border-gray-200 focus:border-[#008BBC] focus:ring-[#008BBC]/20';
  }

  const trustBadges = [
    {
      tone: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
      Icon: Leaf,
      title: t('step13c.trust_natural_title', '100% natural'),
      sub: t('step13c.trust_natural_sub', 'Sin aditivos'),
    },
    {
      tone: 'bg-sky-50 text-[#008BBC] ring-sky-100',
      Icon: Stethoscope,
      title: t('step13c.trust_vets_title', 'Formulación veterinaria'),
      sub: t('step13c.trust_vets_sub', 'Por expertos nutricionistas'),
    },
    {
      tone: 'bg-amber-50 text-amber-500 ring-amber-100',
      Icon: SnowflakeOff,
      title: t('step13c.trust_rating_title', 'Sin frío'),
      sub: t('step13c.trust_rating_sub', 'Sin complicaciones'),
    },
    {
      tone: 'bg-[#E8F6FA] text-[#008BBC] ring-[#008BBC]/10',
      Icon: RotateCcw,
      title: t('step13c.trust_cancel_title', 'Sin compromiso'),
      sub: t('step13c.trust_cancel_sub', 'Pausa o cancela'),
    },
  ];

  return (
    <div className="min-h-screen bg-[#F7F4EE] flex flex-col">
      <Phase2Header step={5} />
      <StepNav currentStep={3} completedSteps={[0, 1, 2]} onStepClick={handleStepClick} />

      {/* funnel-bottom-clearance: clears the fixed mobile bar (its measured height) plus the LiveChat bubble */}
      <div className="flex-1 px-4 py-4 funnel-bottom-clearance lg:pb-4 max-w-5xl mx-auto w-full">
        {updateCtx.isUpdateMode && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 mb-4 text-center">
            <p className="font-fredoka text-sm font-semibold text-amber-800">
              {t('step13c.update_banner', 'Actualizando plan de {nombre}', { nombre: formData.nombre || 'tu perro' })}
            </p>
            <p className="font-poppins text-xs text-amber-700/80 mt-1 leading-snug">
              {t('step13c.update_subtitle', 'Modifica lo que necesites y confirma al final para guardar los cambios.')}
            </p>
          </div>
        )}
        {addon.isAddon && (
          <div className="bg-[#008BBC]/10 border border-[#008BBC]/30 rounded-2xl px-4 py-3 mb-4 text-center">
            <p className="font-fredoka text-sm font-semibold text-[#008BBC]">
              🐾 {t('step13c.addon_banner', 'Añadiendo {nombre} a tu plan', { nombre: formData.nombre || 'tu peludo' })}
            </p>
            <p className="font-poppins text-xs text-black/60 mt-1 leading-snug">
              {t(
                'step13c.addon_subtitle',
                'No hay cargo hoy. {nombre} entra en tu próxima renovación junto con el resto de tu familia.',
                { nombre: formData.nombre || 'tu peludo' },
              )}
            </p>
          </div>
        )}

        {!multipetEnabled && !addon.isAddon && petsCompleted.length > 0 && platform !== 'prestashop' && (
          <div className="bg-amber-50 border border-amber-300 rounded-2xl p-4 text-center mb-4">
            <p className="font-poppins text-sm text-amber-900">
              ⚠️{' '}
              {t(
                'step13c.multipet_disabled_with_pets',
                'El pedido multi-mascota ya no está disponible. Deja solo una mascota en el carrito (puedes quitar las demás desde el resumen) y completa su pedido; después repite el test para la siguiente.',
              )}
            </p>
          </div>
        )}

        {multipetEnabled && multiPetIntent && petsCompleted.length === 0 && platform !== 'prestashop' && (
          <div className="bg-white border border-[#008BBC]/30 rounded-2xl p-5 text-center">
            <p className="font-fredoka text-lg font-semibold text-[#1a1a1a] mb-1">
              🐕 {t('step13c.multipet_block_title', '¿Añadimos la segunda mascota?')}
            </p>
            <p className="font-poppins text-sm text-black/60 mb-4">
              {t(
                'step13c.multipet_block_subtitle',
                'Prepara ahora su plan. Cuando estén todas tus mascotas, completarás un único pedido total.',
                { nombre: formData.nombre || 'tu mascota' },
              )}
            </p>
            <button
              type="button"
              onClick={handleAddPetFromCheckout}
              className="px-5 py-2 rounded-xl border border-[#008BBC] text-[#008BBC] font-poppins text-sm font-medium hover:bg-[#008BBC]/10 transition-colors"
            >
              {t('step13c.multipet_block_cta', 'Añadir segunda mascota')}
            </button>
          </div>
        )}

        {multipetEnabled &&
          multiPetIntent &&
          petsCompleted.length > 0 &&
          petsCompleted.length < MAX_PETS_PER_ORDER &&
          platform !== 'prestashop' &&
          morePetsChoice !== 'no' && (
            <div className="bg-white border border-[#008BBC]/30 rounded-2xl p-4 sm:p-5 mb-4">
              <p className="font-fredoka text-lg font-semibold text-[#1a1a1a] mb-1 text-center">
                {t('step13c.multipet_more_title', '¿Quieres añadir otra mascota?')}
              </p>
              <p className="font-poppins text-sm text-black/60 mb-4 text-center leading-snug">
                {t('step13c.multipet_more_subtitle', 'Puedes preparar el plan de una mascota más antes de pagar.')}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={handleAddPetFromCheckout}
                  className="rounded-xl border border-[#008BBC] bg-[#008BBC] px-3 py-2.5 font-poppins text-sm font-semibold text-white hover:bg-[#007aa8] transition-colors"
                  aria-pressed={morePetsChoice === 'yes'}
                >
                  {t('step13c.multipet_more_yes', 'Sí')}
                </button>
                <button
                  type="button"
                  onClick={handleNoMorePets}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 font-poppins text-sm font-semibold text-gray-700 hover:border-[#008BBC]/40 transition-colors"
                  aria-pressed={false}
                >
                  {t('step13c.multipet_more_no', 'No')}
                </button>
              </div>
            </div>
          )}

        <div className="hidden text-center mb-4 lg:block">
          <h1 className="font-fredoka text-xl sm:text-2xl font-bold text-[#1a1a1a]">{heroTitle}</h1>
        </div>

        {/* display:contents en mobile: convierte los hijos de LEFT col en siblings del flex padre para reordenarlos con `order-N`. En desktop vuelve a flex normal. */}
        <div className="flex flex-col lg:grid lg:grid-cols-[1fr_380px] gap-6">
          {/* LEFT COLUMN */}
          <div className="contents lg:flex lg:flex-col lg:gap-4">
            <div className="order-1 lg:order-none bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
              <h2 className="font-fredoka text-lg sm:text-xl font-bold text-[#1a1a1a] leading-tight">
                🌿{' '}
                {isMultiPet
                  ? t('step13c.hero_title_multipet', '{nombres} están a un paso de comer como se merecen', {
                      nombres: [...petsCompleted.map((p) => p.nombre), formData.nombre || 'tu perro'].join(' y '),
                    })
                  : t('step13c.hero_title', '{nombre} está a un paso de comer como se merece', {
                      nombre: formData.nombre || 'tu peludo',
                    })}
              </h2>
              <p className="hidden lg:block font-poppins text-sm text-black/60 mt-1.5 leading-snug">
                {t(
                  'step13c.hero_subtitle',
                  'Comida natural cocinada para que crezca sano, con pelaje brillante y mucha energía.',
                )}
              </p>
            </div>

            {!multipetEnabled && !addon.isAddon && petsCompleted.length === 0 && platform !== 'prestashop' && (
              <div
                role="note"
                className="order-1 lg:order-none overflow-hidden rounded-2xl border border-[#008BBC]/20 bg-white text-center"
              >
                {/* Disclosure: the whole card toggles the detail; the chevron tells there is more and flips when open. */}
                <button
                  type="button"
                  onClick={() => setShowMultipetNoteDetail((open) => !open)}
                  aria-expanded={showMultipetNoteDetail}
                  className="flex w-full items-center justify-center gap-1.5 p-4 font-poppins text-sm font-semibold text-[#008BBC] hover:bg-[#008BBC]/5 transition-colors"
                >
                  <span aria-hidden="true">🐾</span>
                  <span>{t('step13c.multipet_disabled_note_title', '¿Tienes más de una mascota?')}</span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 transition-transform ${showMultipetNoteDetail ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </button>
                {showMultipetNoteDetail && (
                  <p className="px-4 pb-4 pt-3 font-poppins text-sm text-black/60">
                    {t(
                      'step13c.multipet_disabled_note',
                      'Completa este pedido y repite el test para la siguiente: tendrá su propia suscripción independiente.',
                    )}
                  </p>
                )}
              </div>
            )}

            {checkoutPaused && (
              <div role="alert" className="bg-amber-50 border border-amber-200 rounded-2xl p-5 shadow-sm space-y-3">
                <h3 className="font-fredoka text-base font-semibold text-amber-900">
                  ⏸️ {t('step13c.paused_title', 'Checkout temporalmente pausado')}
                </h3>
                <p className="font-poppins text-sm text-amber-900/80 leading-snug">{checkoutPausedMessage}</p>
                {whatsappUrl && (
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-5 py-2.5 rounded-xl font-fredoka text-sm font-medium bg-[#25D366] text-white hover:bg-[#1ebe5b] shadow-sm transition-all"
                  >
                    {t('step13c.paused_whatsapp_cta', 'Contactar por WhatsApp')}
                  </a>
                )}
              </div>
            )}

            {/* Email: sticky en mobile solo mientras el campo está vacío — en cuanto el usuario escribe, vuelve a flujo normal */}
            <div
              className={`order-2 lg:order-none ${!email ? 'sm:sticky sm:top-0 sm:z-30 lg:static lg:z-auto' : ''} bg-white rounded-2xl p-5 border border-gray-100 shadow-sm`}
            >
              <label
                htmlFor="checkout-email"
                className="flex items-baseline gap-1 font-fredoka text-base font-semibold text-black mb-1.5"
              >
                <span>{t('step13c.email_label', 'Email')}</span>
                <span className="text-red-500">*</span>
                <span className="ml-1 font-poppins text-xs font-normal text-black/40">
                  {t('step13c.email_required_hint', 'obligatorio')}
                </span>
              </label>
              <input
                id="checkout-email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder={t('step13c.email_placeholder', 'tu@email.com')}
                className={`w-full px-4 py-3 font-poppins text-base border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${emailBorderClasses} ${shouldShakeEmail ? 'animate-[emailShake_0.6s_ease-in-out]' : ''}`}
              />
              {email.trim().length > 0 && (
                <div className="mt-1.5 flex items-center gap-1">
                  {isEmailValid ? (
                    <>
                      <span className="text-green-500 text-sm font-bold">✓</span>
                      <span className="font-poppins text-xs text-green-600">
                        {t('step13c.email_valid', 'Formato correcto')}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-red-500 text-sm font-bold">✗</span>
                      <span className="font-poppins text-xs text-red-500">
                        {t('step13c.email_invalid_format', 'Introduce un email válido (ej: nombre@dominio.com)')}
                      </span>
                    </>
                  )}
                </div>
              )}

              {showDonationWidget && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <DonationSummary
                    t={t}
                    donationMode={donationMode}
                    donateRecurringAmount={donateRecurringAmount}
                    donateOneTimeAmount={donateOneTimeAmount}
                    donateCustomAmount={donateCustomAmount}
                    onToggleRecurring={handleToggleRecurringDonation}
                    onModify={handleOpenDonationModal}
                    hasRecurringDonation={hasRecurringDonation}
                  />
                </div>
              )}

              {catalogUnavailable && (
                <div className="mt-2 font-poppins text-xs text-orange-600 flex items-center gap-1">
                  <span>⚠️</span>
                  <span>
                    {t(
                      'step13c.config_error',
                      'El catálogo no está disponible en este momento. Por favor, recarga la página o contacta con nosotros.',
                    )}
                  </span>
                </div>
              )}
              {error && !(errorIsEmail && isEmailValid) && (
                <div className="mt-2 font-poppins text-xs text-red-500 flex items-center gap-1">
                  <span>⚠️</span>
                  <span>{error}</span>
                </div>
              )}
            </div>

            <div className="order-4 lg:order-none rounded-2xl border border-[#008BBC]/12 bg-white p-2.5 shadow-[0_10px_28px_rgba(0,0,0,0.05)]">
              <div className="grid grid-cols-2 gap-2">
                {trustBadges.map((item, i) => (
                  <div
                    key={i}
                    className="min-w-0 rounded-xl border border-black/[0.04] bg-[#FFFCF7] px-2.5 py-2.5 shadow-sm"
                  >
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ring-1 ${item.tone}`}
                      >
                        <item.Icon size={17} strokeWidth={2.4} fill="none" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-fredoka text-[11px] font-bold leading-tight text-[#1a1a1a] sm:text-[13px]">
                          {item.title}
                        </p>
                        <p className="mt-0.5 truncate font-poppins text-[9px] leading-tight text-black/45 sm:text-[10px]">
                          {item.sub}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="order-5 lg:order-none">
              <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                <span className="font-poppins text-[10px] text-black/40 sm:text-[11px]">
                  🔒 {t('step13c.pay_secure_short', 'Pago seguro')}
                </span>
                {/* Visa */}
                <span className="inline-flex h-5 items-center justify-center rounded border border-gray-200 bg-white px-1.5 sm:h-6 sm:px-2">
                  <svg
                    width="26"
                    height="12"
                    viewBox="0 0 32 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="sm:h-[14px] sm:w-8"
                  >
                    <text
                      x="0"
                      y="12"
                      fontFamily="Arial, sans-serif"
                      fontWeight="bold"
                      fontSize="14"
                      fill="#1A1F71"
                      letterSpacing="-0.5"
                    >
                      VISA
                    </text>
                  </svg>
                </span>
                {/* Mastercard */}
                <span className="inline-flex h-5 w-8 items-center justify-center rounded border border-gray-200 bg-white sm:h-6 sm:w-10">
                  <svg
                    width="23"
                    height="15"
                    viewBox="0 0 28 18"
                    xmlns="http://www.w3.org/2000/svg"
                    className="sm:h-[18px] sm:w-7"
                  >
                    <circle cx="10" cy="9" r="8" fill="#EB001B" />
                    <circle cx="18" cy="9" r="8" fill="#F79E1B" />
                    <path
                      d="M14 3.2A7.96 7.96 0 0 1 17.2 9 7.96 7.96 0 0 1 14 14.8 7.96 7.96 0 0 1 10.8 9 7.96 7.96 0 0 1 14 3.2z"
                      fill="#FF5F00"
                    />
                  </svg>
                </span>
                {/* PayPal */}
                <span className="inline-flex h-5 items-center justify-center rounded border border-gray-200 bg-white px-1 sm:h-6 sm:px-1.5">
                  <span className="font-poppins text-[9px] font-bold leading-none sm:text-[10px]">
                    <span style={{ color: '#003087' }}>Pay</span>
                    <span style={{ color: '#009cde' }}>Pal</span>
                  </span>
                </span>
                {/* Bizum */}
                <span className="inline-flex h-5 items-center justify-center rounded border border-gray-200 bg-white px-1.5 sm:h-6 sm:px-2">
                  <span className="text-[9px] font-bold text-[#00c0a0] sm:text-[10px]">Bizum</span>
                </span>
              </div>
            </div>

            <p className="order-6 lg:order-none font-poppins text-[11px] text-center text-black/45 leading-relaxed px-2">
              {t(
                'step13c.subscription_notice',
                'Al confirmar activas una suscripción con renovación automática cada {days} días. Puedes pausarla o cancelarla cuando quieras.',
                { days: SUBSCRIPTION_DAYS },
              )}
            </p>

            {/* CTA desktop */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={ctaDisabled}
              aria-disabled={ctaDisabled}
              className={`order-6 lg:order-none hidden lg:block w-full py-4 rounded-xl font-fredoka text-xl font-bold transition-all ${
                !ctaDisabled
                  ? 'bg-[#008BBC] text-white hover:bg-[#007aa8] shadow-xl hover:shadow-2xl active:scale-[0.98]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                t('step13c.processing', 'Procesando...')
              ) : isCalculating ? (
                t('step13c.calculating', 'Actualizando precios...')
              ) : (
                <>{submitLabel} &rarr;</>
              )}
            </button>

            {pricingError && !isCalculating && (
              <div
                role="alert"
                className="order-6 lg:order-none hidden lg:block bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-center"
              >
                <p className="font-poppins text-xs text-amber-800">
                  {t(
                    'step13c.pricing_error',
                    'Seguimos usando el precio calculado. Lo verificaremos antes de crear el pedido.',
                  )}
                </p>
              </div>
            )}

            {!isCalculating && (
              <StockAdjustmentsNotice
                items={checkoutAdjustments}
                footer={
                  submitAdjustments.length > 0
                    ? t('stock.notice.recheck', 'Hemos actualizado el resumen; revísalo y vuelve a confirmar.')
                    : undefined
                }
                onChangeRecipes={() => setCurrentStep(RECIPE_PICKER_STEP)}
                className="order-6 lg:order-none"
              />
            )}

            <div className="order-7 lg:order-none text-center">
              <button
                type="button"
                onClick={handleBack}
                className="font-poppins text-sm text-black/40 hover:text-black/60 transition-colors"
              >
                &larr; {t('step13c.back', 'Volver atrás')}
              </button>
            </div>

            <p className="order-8 lg:order-none font-poppins text-[11px] text-black/30 text-center leading-snug">
              {t('step13c.legal_prefix', 'Al continuar, aceptas nuestras')}{' '}
              <a
                href={config.funnel_config.legal_urls?.terms || DEFAULT_APP_CONFIG.funnel_config.legal_urls!.terms}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#008BBC] underline"
              >
                {t('step13c.terms_link', 'Condiciones')}
              </a>{' '}
              {t('step13c.legal_and', 'y')}{' '}
              <a
                href={config.funnel_config.legal_urls?.privacy || DEFAULT_APP_CONFIG.funnel_config.legal_urls!.privacy}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#008BBC] underline"
              >
                {t('step13c.privacy_link', 'Politica de Privacidad')}
              </a>
            </p>
          </div>

          {/* RIGHT COLUMN: Cart */}
          <CartSummary
            t={t}
            isTransition={isTransition}
            transitionScheduleDays={transitionScheduleDays}
            INITIATION_DAYS={INITIATION_DAYS}
            SUBSCRIPTION_DAYS={SUBSCRIPTION_DAYS}
            INITIATION_PACK_DISCOUNT={INITIATION_PACK_DISCOUNT}
            MONTHLY_SUBSCRIPTION_DISCOUNT={MONTHLY_SUBSCRIPTION_DISCOUNT}
            initiationPrice={initiationPrice}
            initiationOriginalPrice={initiationOriginalPrice}
            monthlyDiscountedPrice={monthlyDiscountedPrice}
            supplementsOneOffTotal={supplementsOneOffTotal}
            todayTotal={displayTodayTotal}
            shippingPrice={shippingPrice}
            shippingUnavailable={shippingUnavailable}
            shippingAddressPending={shippingAddressPending}
            shippingControl={
              isShippingActive && shippingRules ? (
                <ShippingSelector
                  t={t}
                  enabledCountries={enabledCountries}
                  shippingCountryUI={shippingCountryUI}
                  shippingPostalCodeUI={shippingPostalCodeUI}
                  shippingResult={shippingResult}
                  addressPending={shippingAddressPending}
                  postalCodeApplies={countryHasZones}
                  unsupportedMessage={shippingRules.unsupported_message}
                  onCountryChange={(country) => {
                    // A postal code belongs to one country: never carry it over to the next.
                    setShippingCountryUI(country);
                    setShippingPostalCodeUI('');
                    setShippingDetectionSource('manual');
                  }}
                  onPostalCodeChange={(postalCode) => {
                    setShippingPostalCodeUI(postalCode);
                    setShippingDetectionSource('postal_code');
                  }}
                />
              ) : undefined
            }
            monthlyShippingFree={monthlyShippingFree}
            monthlyShippingPrice={monthlyShippingPrice}
            discountAmount={discountAmount}
            dailyCost={dailyCost}
            savings={savings}
            effectiveRecipeLines={effectiveRecipeLines}
            hasSupplements={hasSupplements}
            selectedSupplements={formData.selectedSupplements || []}
            supplementRules={supplementRules}
            donateRecurringAmount={donateRecurringAmount}
            donateOneTimeAmount={donateOneTimeAmount}
            donateCustomAmount={donateCustomAmount}
            donationMode={donationMode}
            updateFormData={updateFormData as (data: Record<string, unknown>) => void}
            currentDistribution={formData.initiationPackDistribution}
            recommendedDistribution={formData.recommendedInitiationPackDistribution}
            onUpdateCurrentDistribution={
              config.pack_rules?.allow_custom_initiation_distribution !== false
                ? handleUpdateCurrentDistribution
                : undefined
            }
            stockLimits={stockLimits}
            canRemoveRecipes={config.pack_rules?.allow_recipe_removal_in_checkout !== false}
            onOpenEditMenus={() => {
              // Snapshot refresh so the limits are at most cron + catalog-cache old.
              void refreshConfig({ silent: true });
            }}
            isMultiPet={isMultiPet}
            petsCompleted={petsCompleted}
            currentNombre={formData.nombre || ''}
            currentRaza={formData.raza ?? null}
            recipes={recipes}
            multiPetTotals={isMultiPet ? multiPetTotals : undefined}
            onEditRecipes={handleEditRecipes}
            onEditCompletedPetPack={(petId) => restorePetForEditing(petId, RECIPE_PICKER_STEP)}
            onRemoveCompletedPetPack={removePetSnapshot}
            onRemoveCurrentPetPack={petsCompleted.length > 0 ? handleRemoveCurrentPetFromCheckout : undefined}
          />
        </div>
      </div>

      {/* Fixed CTA mobile */}
      <div
        ref={mobileCtaRef}
        className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.05)] z-20"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="font-poppins text-[11px] text-black/50">
              {isTransition
                ? t('step13c.sticky_today', 'Total a pagar hoy')
                : t('step13c.sticky_month', 'Total mensual')}
            </span>
            <span className="font-fredoka text-xl font-bold text-[#1a1a1a]">{formatPrice(displayTodayTotal)}</span>
          </div>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={ctaDisabled}
            aria-disabled={ctaDisabled}
            className={`w-full py-3 px-4 rounded-xl font-fredoka text-base font-bold whitespace-nowrap transition-all ${
              !ctaDisabled
                ? 'bg-[#008BBC] text-white hover:bg-[#007aa8] shadow-lg active:scale-[0.98]'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? (
              t('step13c.processing', 'Procesando...')
            ) : isCalculating ? (
              t('step13c.calculating', 'Actualizando precios...')
            ) : (
              <>{submitLabel} &rarr;</>
            )}
          </button>
          {checkoutNeedsEmail && (
            <p className="font-poppins text-[11px] text-amber-700 text-center mt-1.5">
              {email.trim().length > 0
                ? t('step13c.mobile_cta_email_invalid', 'Revisa el formato del email para continuar.')
                : t('step13c.mobile_cta_email_required', 'Introduce tu email para continuar.')}
            </p>
          )}
          {pricingError && !isCalculating && (
            <p role="alert" className="font-poppins text-[11px] text-amber-700 text-center mt-1.5">
              {t(
                'step13c.pricing_error',
                'Seguimos usando el precio calculado. Lo verificaremos antes de crear el pedido.',
              )}
            </p>
          )}
          <div className="text-center mt-1.5">
            <button
              type="button"
              onClick={handleBack}
              className="font-poppins text-[11px] text-[#aaa] hover:text-[#666] transition-colors"
            >
              &larr; {t('step13c.back', 'Volver atrás')}
            </button>
          </div>
        </div>
      </div>

      <DonationModal
        t={t}
        isOpen={donationModalOpen}
        infoOpen={donationInfoOpen}
        onClose={handleCloseDonationModal}
        onCloseInfo={handleCloseDonationInfo}
        onOpenInfo={handleOpenDonationInfo}
        showDonationToggle={showDonationToggle}
        donationMode={donationMode}
        onDonationModeChange={setDonationMode}
        hasRecurringDonation={hasRecurringDonation}
        hasAnyOneTimeDonation={hasAnyOneTimeDonation}
        donateRecurringAmount={donateRecurringAmount}
        onRecurringAmountChange={setDonateRecurringAmount}
        donateOneTimeAmount={donateOneTimeAmount}
        onOneTimeAmountChange={setDonateOneTimeAmount}
        donateCustomAmount={donateCustomAmount}
        onCustomAmountChange={setDonateCustomAmount}
        donationOneTimeVariantIds={donationOneTimeVariantIds}
        donationInfoUrl={config.donation_rules?.info_url || DEFAULT_APP_CONFIG.donation_rules.info_url}
      />

      {/* Keyframe del shake del campo email — inline aquí para no contaminar el CSS global con una animación de un solo componente. */}
      <style>{`
        @keyframes emailShake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-5px); }
          30% { transform: translateX(5px); }
          45% { transform: translateX(-4px); }
          60% { transform: translateX(4px); }
          75% { transform: translateX(-2px); }
          90% { transform: translateX(2px); }
        }
      `}</style>
    </div>
  );
}
